// File path: /core/frontend/src/services/cognitoAttributes.ts
//
// Which Cognito user attributes the browser may write (P-HARD H5, S3).
//
// Identity on the server is Cognito *groups* + core.users; the authorizer reads
// no custom attribute. A user must never be able to set their own role or
// tenant, so the frontend writes only these standard attributes — the same list
// the app clients' WriteAttributes allow once queue item A2c lands:
//   given_name, family_name, phone_number, preferred_username.
// No `custom:*` attribute is ever written from the browser.
//
// Dependency-free on purpose, so it can be unit-tested without Amplify.

/** What the first-login (NEW_PASSWORD_REQUIRED) form sends — nothing else. */
export const FIRST_LOGIN_ATTRIBUTES = ['given_name', 'family_name', 'preferred_username'] as const;

/** What a signed-in user may change about themselves (= the app clients' WriteAttributes). */
export const SELF_WRITABLE_ATTRIBUTES = ['given_name', 'family_name', 'phone_number', 'preferred_username'] as const;

export type FirstLoginAttribute = typeof FIRST_LOGIN_ATTRIBUTES[number];
export type SelfWritableAttribute = typeof SELF_WRITABLE_ATTRIBUTES[number];
export type WritableUserAttributes = Partial<Record<SelfWritableAttribute, string>>;

export interface NewPasswordRequiredData {
  given_name: string;
  family_name: string;
  /** Defaults to the user's email on the first-login form. */
  preferred_username: string;
}

/** The attribute map Auth.completeNewPassword sends: exactly FIRST_LOGIN_ATTRIBUTES. */
export function buildFirstLoginAttributes(data: NewPasswordRequiredData): Record<FirstLoginAttribute, string> {
  return {
    given_name: (data.given_name || '').trim(),
    family_name: (data.family_name || '').trim(),
    preferred_username: (data.preferred_username || '').trim(),
  };
}

/**
 * Keeps only self-writable, string-valued attributes. Anything else — `custom:role`,
 * `custom:tenant_id`, `email`, … — is dropped, whatever the caller passed.
 */
export function selfWritableAttributes(attributes: Record<string, unknown>): WritableUserAttributes {
  const writable: WritableUserAttributes = {};
  for (const name of SELF_WRITABLE_ATTRIBUTES) {
    const value = attributes[name];
    if (typeof value === 'string') {
      writable[name] = value;
    }
  }
  return writable;
}

/**
 * The email of a user in the NEW_PASSWORD_REQUIRED challenge — the default for
 * preferred_username. Amplify puts the user's current attributes in
 * `challengeParam.userAttributes`; the pool's usernames are UUIDs, so the
 * username is only a fallback when it is itself an email.
 */
export function firstLoginEmail(cognitoUser: any): string {
  const email = cognitoUser?.challengeParam?.userAttributes?.email;
  if (typeof email === 'string' && email) {
    return email;
  }
  const username = cognitoUser?.username;
  return typeof username === 'string' && username.includes('@') ? username : '';
}
